import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  FC,
  ReactNode,
} from "react";
import { notification } from "antd";

export const noticeTypes = ["info", "success", "warning", "error"] as const;
export type NoticeType = (typeof noticeTypes)[number]; // "info" | "success" | "warning" | "error"

export interface NotificationContextType {
  showNotification: (
    type: NoticeType,
    content: ReactNode,
    duration?: number,
  ) => void;

  enqueueNotification: (
    type: NoticeType,
    content: ReactNode,
    duration?: number,
  ) => void;
}

const NotificationContext = createContext<NotificationContextType>({
  showNotification: () => {},
  enqueueNotification: () => {},
});

type ProviderProps = { children: ReactNode };
const DEFAULT_DURATION = 2; // секунды

export const NotificationProvider: FC<ProviderProps> = ({ children }) => {
  const [api, contextHolder] = notification.useNotification();

  const [queue, setQueue] = useState<
    { type: NoticeType; content: ReactNode; duration?: number }[]
  >([]);

  const showNotification = useCallback(
    (
      type: NoticeType,
      content: ReactNode,
      duration: number = DEFAULT_DURATION,
    ) => {
      api.open({
        type,
        message: null,
        description: content,
        duration,
        icon: null,
        placement: "topRight",
      });
    },
    [api],
  );

  const enqueueNotification = useCallback(
    (type: NoticeType, content: ReactNode, duration?: number) => {
      setQueue((prev) => [...prev, { type, content, duration }]);
    },
    [],
  );

  useEffect(() => {
    if (queue.length === 0) return;

    let timerId: NodeJS.Timeout | null = null;

    const process = () => {
      const { type, content, duration } = queue[0];
      showNotification(type, content, duration);
      setQueue((prev) => prev.slice(1));

      timerId = setTimeout(process, (duration ?? DEFAULT_DURATION) * 1000 + 50);
    };

    process();

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [queue, showNotification]);

  const contextValue: NotificationContextType = {
    showNotification,
    enqueueNotification,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
